// Typed client for the producer/company registry (/api/dpp/registry/*) — our
// internal acquisition CRM. Operator-gated server-side.
import { api } from "./api";

export type CompanyStatus =
  | "new"
  | "contacted"
  | "interested"
  | "customer"
  | "declined";

export interface Company {
  id: string;
  name: string;
  legal_name: string;
  registry_id: string;
  country: string;
  industry: string;
  sub_type: string;
  size: string;
  address: string;
  postal_code: string;
  city: string;
  region: string;
  lat: number;
  lng: number;
  email: string;
  phone: string;
  website: string;
  social: Record<string, string> | null;
  description: string;
  languages: string[] | null;
  certifications: string[] | null;
  brands: string[] | null;
  attributes: Record<string, unknown> | null;
  source: string;
  sources: unknown[] | null;
  source_url: string;
  last_enriched: string;
  confidence: number;
  status: CompanyStatus | "";
  owner: string;
  notes: string;
  created: string;
  updated: string;
}

export interface Contact {
  id: string;
  company: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  linkedin: string;
  source: string;
  confidence: number;
  notes: string;
}

export interface CompanyFilter {
  q?: string;
  status?: string;
  industry?: string;
  country?: string;
  sub_type?: string;
  page?: number;
  perPage?: number;
}

function qs(f: CompanyFilter): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const companies = {
  list: (f: CompanyFilter = {}) =>
    api.get<{ companies: Company[]; page: number; perPage: number }>(
      `/registry/companies${qs(f)}`,
    ),
  get: (id: string) =>
    api.get<{ company: Company; contacts: Contact[] }>(
      `/registry/companies/${id}`,
    ),
  create: (body: Partial<Company>) =>
    api.post<Company>("/registry/companies", body),
  update: (id: string, body: Partial<Company>) =>
    api.patch<Company>(`/registry/companies/${id}`, body),
  remove: (id: string) => api.del<void>(`/registry/companies/${id}`),
  addContact: (companyId: string, body: Partial<Contact>) =>
    api.post<Contact>(`/registry/companies/${companyId}/contacts`, body),
  removeContact: (contactId: string) =>
    api.del<void>(`/registry/contacts/${contactId}`),
  importGouvFr: (body: {
    naf?: string;
    departements?: string;
    industry?: string;
    region?: string;
    maxPages?: number;
  }) =>
    api.post<{ fetched: number; created: number; updated: number; skipped: number }>(
      "/acquisition/import/gouv-fr",
      body,
    ),
  importComite: (body: { industry?: string; region?: string; maxPages?: number }) =>
    api.post<{ fetched: number; created: number; updated: number; skipped: number }>(
      "/acquisition/import/comite-champagne",
      body,
    ),
  duplicates: () =>
    api.get<{ pairs: DupPair[] }>("/registry/duplicates").then((r) => r.pairs ?? []),
  merge: (keepId: string, dropId: string) =>
    api.post<{ status: string }>("/registry/merge", { keepId, dropId }),
};

export interface CompanyBrief {
  id: string;
  name: string;
  registryId: string;
  source: string;
  city: string;
  postalCode: string;
  website: string;
  email: string;
}

export interface DupPair {
  a: CompanyBrief;
  b: CompanyBrief;
  reason: string;
  score: number;
}
